import React from "react";
import { Text, type TextProps } from "react-native";
import { useLocaleFont } from "@/hooks/useLanguage";
import { useI18n } from "@/context/I18nContext";
import { cn } from "@/lib/utils";

export type FontWeight = "regular" | "medium" | "semibold" | "bold";

export interface AppTextProps extends TextProps {
  /** Selects the correct weight variant for the active locale font. */
  weight?: FontWeight;
  className?: string;
}

/**
 * Drop-in replacement for React Native's <Text>.
 *
 * Automatically applies the correct locale font family so Arabic text renders
 * with the Saudi typeface and English/Chinese text renders with Inter Display,
 * without any opt-in at the call site.
 *
 * Use the `weight` prop instead of Tailwind's font-bold / font-semibold /
 * font-medium utilities — those only affect fontWeight on web but do not swap
 * the fontFamily on native.
 *
 * @example
 * <AppText weight="semibold" className="text-base text-dark-text-50">
 *   Hello
 * </AppText>
 */
export function AppText({ weight = "regular", className = "", ...props }: AppTextProps) {
  const { isRTL } = useI18n();
  const base = useLocaleFont(); // "font-sans" | "font-saudi"
  const fontClass = weight === "regular" ? base : `${base}-${weight}`;
  return <Text className={cn(fontClass, isRTL && "text-end", className)} {...props} />;
}
